import clsx from 'clsx'
import { useEffect } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { FvButton, Icon, type IconName } from '@fv/client-components'
import { VITE_ENV } from '@fv/client-core'
import type {
  SystemGeneratedNotification,
  SystemNotification,
  SystemNotificationType,
} from '@fv/models'
import type { DTO } from '@fv/models/core'

export type SystemNotificationsProps = {
  notifications?: Array<DTO<SystemNotification>>
  canDismissEnvironmentBanner?: boolean
}

const environmentLabels: Record<string, string> = {
  sandbox: 'Sandbox',
  staging: 'Staging',
}

const getEnvironmentNotificationId = (environment: string): string =>
  `environment-notification-${environment}`

export const SystemNotifications = ({
  notifications,
  canDismissEnvironmentBanner = false,
}: SystemNotificationsProps) => {
  const [seenNotifications, setSeenNotifications] = useLocalStorage<string[]>(
    'system-notifications',
    [],
    {
      deserializer: v => {
        if (!v) return []
        try {
          return JSON.parse(v)
        } catch {
          return []
        }
      },
    },
  )

  const onHide = (id: string) => {
    setSeenNotifications(p => {
      if (p.includes(id)) return p
      return [...p, id]
    })
  }

  useEffect(() => {
    // make sure we don't fill up local storage ... 500 seems like a reasonable limit
    if (seenNotifications.length > 500) {
      setSeenNotifications([])
    }
  }, [seenNotifications, setSeenNotifications])

  const notificationsToShow =
    notifications?.filter(n => !seenNotifications.includes(n._id.toString())) ??
    []
  const environmentLabel = environmentLabels[VITE_ENV]
  const environmentNotificationId = getEnvironmentNotificationId(VITE_ENV)
  const showEnvironmentBanner =
    !!environmentLabel && !seenNotifications.includes(environmentNotificationId)

  if (!notificationsToShow.length && !showEnvironmentBanner) return null

  return (
    <>
      {showEnvironmentBanner && (
        <SystemNotificationBanner
          notification={{
            _id: environmentNotificationId ?? 'environment-notification',
            text: `You are currently in the Freightview <strong>${environmentLabel}</strong> environment.`,
            type: 'environment',
            expiration: '',
          }}
          onHide={
            canDismissEnvironmentBanner
              ? () => onHide(environmentNotificationId)
              : undefined
          }
        />
      )}
      {notificationsToShow.map(n => (
        <SystemNotificationBanner
          notification={n}
          key={n._id.toString()}
          onHide={() => onHide(n._id.toString())}
        />
      ))}
    </>
  )
}

type ThemeConfig = Record<
  SystemNotificationType,
  {
    themeIcon: IconName
  }
>

const themeConfig: ThemeConfig = {
  default: {
    themeIcon: 'sticky-note',
  },
  warning: {
    themeIcon: 'hand-paper',
  },
  success: {
    themeIcon: 'check',
  },
  holiday: {
    themeIcon: 'calendar-day',
  },
  info: {
    themeIcon: 'info-circle',
  },
  release: {
    themeIcon: 'info-circle',
  },
  environment: {
    themeIcon: 'server',
  },
}
type Props = {
  notification: DTO<SystemNotification> | SystemGeneratedNotification
  onHide?: () => void
}
const SystemNotificationBanner = ({ notification, onHide }: Props) => {
  const type = notification.type
  const themeIcon = themeConfig[type].themeIcon

  return (
    <div
      className={clsx('flex flex-wrap px-6 py-4 border-b', {
        'bg-white border-fv-gray': type === 'default',
        'bg-[#ffeeee] border-red-200':
          type === 'warning' || type === 'environment',
        'bg-[#f2fbed] border-[#c3ecbe]': type === 'success',
        'bg-[#f2fbed] border-fv-beer-dark': type === 'holiday',
        'bg-fv-blue-50 border-fv-blue-200':
          type === 'info' || type === 'release',
      })}
    >
      <div className="flex items-start w-full">
        <p className="mb-0 flex items-start">
          <Icon
            icon={themeIcon}
            className={clsx({
              'text-fv-gray': type === 'default',
              'text-fv-orange': type === 'warning' || type === 'environment',
              'text-fv-green': type === 'success',
              'text-fv-beer': type === 'holiday',
              'text-fv-blue': type === 'info' || type === 'release',
            })}
            transform="down-2"
          />
          <span
            className="ml-2"
            dangerouslySetInnerHTML={{
              __html: notification.text.replace(
                /\[(.+?)\]\((.+?)\)/g,
                '<a href="$2" class="underline underline-offset-4 decoration-fv-blue hover:decoration-zinc-600" target="_blank">$1</a>',
              ),
            }}
          />
        </p>
        <div className="leading-[1.4rem] ml-auto pl-10">
          {onHide && (
            <FvButton icon="times" onClick={onHide}>
              Got it, close this message
            </FvButton>
          )}
        </div>
      </div>
    </div>
  )
}
